(() => {
  if (window.FUPromotions) return;
  const zone = 'America/New_York';
  const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const day = (now = new Date()) => {
    const parts = Object.fromEntries(formatter.formatToParts(now).map(part => [part.type, part.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
  const validDate = value => !value || /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
  const eligible = (record, today = day()) => validDate(record.start) && validDate(record.end) && !(record.start && record.end && record.start > record.end) && (!record.start || record.start <= today) && (!record.end || today <= record.end);
  const order = record => Number.isFinite(Number(record.order)) && record.order !== '' ? Number(record.order) : 999999;
  const sortRecords = records => [...records].sort((a, b) => order(a) - order(b) || a.handle.localeCompare(b.handle));
  const nextBoundary = (now = new Date()) => {
    let next = Math.floor(now.getTime() / 3600000) * 3600000 + 3600000;
    while (day(new Date(next)) === day(now)) next += 3600000;
    return next;
  };
  const events = new EventTarget();
  let raw = [], snapshot = { status: 'loading', records: [], date: day() }, pending = null, timer, lastLoad = 0;
  const publish = status => {
    snapshot = { status, records: sortRecords(raw.filter(record => eligible(record))), date: day() };
    events.dispatchEvent(new Event('change'));
    clearTimeout(timer);
    timer = setTimeout(() => { publish(snapshot.status); refresh(); }, Math.max(20, nextBoundary() - Date.now() + 30));
  };
  const extract = catalog => [...catalog.querySelectorAll('template[data-fu-record]')].map(template => ({ ...template.dataset, id: template.dataset.id, handle: template.dataset.handle, start: template.dataset.start || '', end: template.dataset.end || '', featured: template.dataset.featured === 'true', template }));
  const sourceURL = (catalog, page = 1) => {
    const url = new URL(location.href);
    url.hash = '';
    url.searchParams.set('section_id', 'fu-promotion-feed');
    url.searchParams.set(catalog?.dataset.pageParam || 'page', String(page));
    return url;
  };
  const fetchCatalog = async url => {
    const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000), headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Promotion feed unavailable');
    const html = await response.text();
    const catalog = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fu-promo-catalog]');
    if (!catalog) throw new Error('Promotion feed missing');
    return catalog;
  };
  const load = async force => {
    let catalog = document.querySelector('[data-fu-promo-catalog]');
    const records = new Map(), pages = new Set();
    try {
      if (!catalog || force || Number(catalog.dataset.page) !== 1) {
        const url = sourceURL(catalog);
        if (force) url.searchParams.set('_fu_refresh', String(Date.now()));
        catalog = await fetchCatalog(url);
      }
      while (catalog) {
        const page = Number(catalog.dataset.page);
        if (!Number.isInteger(page) || page < 1 || pages.has(page)) throw new Error('Promotion pagination repeated');
        pages.add(page);
        extract(catalog).forEach(record => records.set(record.id || record.handle, record));
        if (!catalog.dataset.next) break;
        const next = new URL(catalog.dataset.next, location.href);
        const url = sourceURL(catalog, next.searchParams.get(catalog.dataset.pageParam || 'page') || page + 1);
        if (force) url.searchParams.set('_fu_refresh', String(Date.now()));
        catalog = await fetchCatalog(url);
      }
      raw = [...records.values()];
      lastLoad = Date.now();
      publish('ready');
    } catch (error) {
      if (records.size) raw = [...records.values()];
      publish(raw.length ? 'partial' : 'error');
      if (window.Shopify?.designMode) console.warn('FU promotions:', error.message);
    }
    return snapshot;
  };
  function refresh(force = true) {
    if (pending) return pending;
    pending = load(force).finally(() => { pending = null; });
    return pending;
  }
  const api = {
    subscribe(callback) { const handler = () => callback(snapshot); events.addEventListener('change', handler); callback(snapshot); return () => events.removeEventListener('change', handler); },
    refresh,
    get snapshot() { return snapshot; },
    rules: Object.freeze({ day, eligible, sortRecords, nextBoundary })
  };
  window.FUPromotions = api;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) { publish(snapshot.status); if (Date.now() - lastLoad > 60000) refresh(); }
  });
  window.addEventListener('pageshow', event => { if (event.persisted) { publish(snapshot.status); refresh(); } });
  document.addEventListener('shopify:section:load', () => refresh());
  refresh(false);
})();


if (!customElements.get('fu-promo-carousel')) {
  customElements.define('fu-promo-carousel', class extends HTMLElement {
    connectedCallback() {
      this.unsubscribe?.();
      this.renderedRecords = null;
      this.unsubscribe = window.FUPromotions.subscribe(state => {
        if (!this.isConnected || state.status === 'loading') return;
        const records = state.records.filter(record => record.featured);
        if (this.renderedRecords?.length === records.length && records.every((record, index) => record.template === this.renderedRecords[index].template)) return;
        const selectedHandle = this.slides?.[this.index]?.dataset.fuHandle;
        const explicitlyPaused = this.paused;
        this.renderedRecords = records;
        this.stopInteractions();
        this.hidden = records.length === 0;
        const stage = this.querySelector('[data-promo-stage]');
        const choices = this.querySelector('.fupc-choices');
        stage.replaceChildren(); choices.replaceChildren();
        records.forEach((record, index) => {
          const slide = record.template.content.querySelector('[data-fu-slide]').firstElementChild.cloneNode(true);
          slide.id = `FUPromo-${this.dataset.section}-${record.handle}`;
          stage.append(slide);
          const choice = document.createElement('button');
          choice.type = 'button'; choice.className = 'fupc-choice'; choice.dataset.promoChoice = '';
          choice.setAttribute('aria-controls', slide.id);
          const brand = document.createElement('span'); brand.className = 'fupc-choice-brand'; brand.textContent = record.brand;
          const detail = document.createElement('span'); detail.textContent = record.tab;
          choice.append(brand, detail); choices.append(choice);
        });
        this.style.setProperty('--fupc-count', records.length);
        this.querySelector('[data-promo-total]').textContent = records.length;
        this.querySelector('[data-promo-count]').textContent = this.dataset.countLabel.replace('[count]', records.length);
        this.querySelectorAll('.fupc-discovery,.fupc-controls,.fupc-arrow').forEach(element => {
          element.style.setProperty('display', records.length > 1 ? '' : 'none', records.length > 1 ? '' : 'important');
        });
        this.total = records.length;
        if (records.length) {
          this.startInteractions();
          if (explicitlyPaused !== undefined) this.paused = explicitlyPaused;
          const preservedIndex = records.findIndex(record => record.handle === selectedHandle);
          this.show(preservedIndex >= 0 ? preservedIndex : 0, false);
        }
      });
    }
    startInteractions() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-promo-slide]')];
      this.choices = [...this.querySelectorAll('[data-promo-choice]')];
      this.toggle = this.querySelector('[data-promo-toggle]');
      this.status = this.querySelector('[data-promo-status]');
      this.number = this.querySelector('[data-promo-number]');
      this.choiceStrip = this.querySelector('.fupc-choices');
      this.index = 0;
      this.inView = false;
      this.hovering = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches || Boolean(window.Shopify?.designMode);
      this.interval = Math.min(12000, Math.max(3000, Number(this.dataset.interval) * 1000 || 5000));
      this.classList.add('is-ready');
      this.choices.forEach((choice, index) => choice.addEventListener('click', () => {
        this.paused = true;
        this.show(index, true);
      }, { signal }));
      [['[data-promo-prev]', -1], ['[data-promo-next]', 1]].forEach(([selector, direction]) => {
        this.querySelector(selector)?.addEventListener('click', () => {
          this.paused = true;
          this.show(this.index + direction, true);
        }, { signal });
      });
      this.toggle?.addEventListener('pointerdown', () => { this.togglePointerPaused = this.paused; }, { signal });
      this.toggle?.addEventListener('click', () => {
        this.paused = !(this.togglePointerPaused ?? this.paused);
        this.togglePointerPaused = null;
        this.schedule();
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') { this.hovering = true; this.schedule(); }
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovering = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => { this.paused = true; this.schedule(); }, { signal });
      this.addEventListener('keydown', event => {
        if (!event.target.closest('[data-promo-choice], [data-promo-prev], [data-promo-next]')) return;
        const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!direction) return;
        event.preventDefault();
        this.paused = true;
        this.show(this.index + direction, true);
        if (event.target.closest('[data-promo-choice]')) this.choices[this.index].focus();
      }, { signal });
      const stage = this.querySelector('[data-promo-stage]');
      stage?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') this.touchStart = { x: event.clientX, y: event.clientY };
      }, { signal });
      stage?.addEventListener('pointerup', event => {
        if (!this.touchStart || event.pointerType !== 'touch') return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 60 && Math.abs(dy) < 70) {
          this.paused = true;
          this.show(this.index + (dx < 0 ? 1 : -1), true);
        }
      }, { signal });
      stage?.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.reducedMotion.addEventListener('change', event => {
        if (event.matches) { this.paused = true; this.schedule(); }
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0) { this.paused = true; this.show(index, true); }
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.show(0, false);
    }
    show(index, announce) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, number) => {
        slide.setAttribute('aria-hidden', String(number !== this.index));
        slide.inert = number !== this.index;
      });
      this.choices.forEach((choice, number) => {
        if (number === this.index) choice.setAttribute('aria-current', 'true');
        else choice.removeAttribute('aria-current');
      });
      if (this.number) this.number.textContent = String(this.index + 1);
      const activeChoice = this.choices[this.index];
      if (activeChoice && this.choiceStrip && this.choiceStrip.scrollWidth > this.choiceStrip.clientWidth) {
        const offset = activeChoice.getBoundingClientRect().left - this.choiceStrip.getBoundingClientRect().left + this.choiceStrip.scrollLeft;
        this.choiceStrip.scrollTo({ left: offset - (this.choiceStrip.clientWidth - activeChoice.offsetWidth) / 2, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      }
      if (announce) this.status.textContent = this.dataset.announcement.replace('[number]', String(this.index + 1)).replace('[total]', this.slides.length) + ' · ' + this.slides[this.index].getAttribute('aria-label');
      this.classList.add('is-changing');
      clearTimeout(this.animationTimer);
      this.animationTimer = setTimeout(() => this.classList.remove('is-changing'), 450);
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      const playing = this.slides.length > 1 && !this.paused && !this.hovering && this.inView && !document.hidden;
      this.dataset.paused = String(this.paused);
      this.dataset.playing = String(playing);
      const label = this.querySelector('[data-promo-toggle-label]');
      if (label) label.textContent = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      if (playing) this.timer = setTimeout(() => this.show(this.index + 1, false), this.interval);
    }
    stopInteractions() {
      clearTimeout(this.timer);
      clearTimeout(this.animationTimer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.classList.remove('is-ready');
    }
    disconnectedCallback() { this.stopInteractions(); this.unsubscribe?.(); }
  });
}

if (!customElements.get('fu-promo-product')) {
  customElements.define('fu-promo-product', class extends HTMLElement {
    connectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = window.FUPromotions.subscribe(state => {
        const record = state.records.find(item => (item.products || '').split(',').includes(this.dataset.productId));
        this.hidden = !record;
        if (!record) return;
        const card = record.template.content.querySelector('[data-fu-card]');
        const text = selector => card.querySelector(selector)?.textContent.trim() || '';
        this.querySelector('[data-fu-product-label]').textContent = record.label;
        this.querySelector('[data-fu-product-value]').textContent = text('.fup2-saving') || text('.fup2-offer') || text('.fup2-price-line strong');
        [['[data-fu-product-urgency]', '.fup2-urgency'], ['[data-fu-product-perk]', '.fup2-perk']].forEach(([target, source]) => {
          const element = this.querySelector(target); element.textContent = text(source); element.hidden = !element.textContent;
        });
        this.querySelector('[data-fu-product-link]').href = record.template.content.querySelector('.fupc-cta').getAttribute('href');
      });
    }
    disconnectedCallback() { this.unsubscribe?.(); }
  });
}
